import { ScheduledDeliverable } from '@/models/ScheduleDeliverable';
import { ScheduledDeliverablesAllTypes } from '@/models/ScheduledDeliverables';

import { ScheduleDeliverableLog } from 'models/ScheduleDeliverableLog';
import { createAsyncAction } from 'typesafe-actions';

import { TransactionItemProps } from '../components/DeliverablesList/components/Actions/CreateEditModal/components/TransactionsTable/types';
import { CreateScheduleDeliverableParamsProps } from '../components/DeliverablesList/components/Actions/CreateEditModal/types';
import { ReportDownloadItem, ScheduleDeliverableParams, ScheduledDeliverablesTestReport, ScheduledDeliverablesTestReports } from '../types';

export type GetScheduledDeliverablesListSuccessPayload = {
  data: ScheduledDeliverablesAllTypes[];
};

export const getScheduledDeliverablesList = createAsyncAction(
  'scheduledDeliverables/fetchScheduledDeliverablesList/request',
  'scheduledDeliverables/fetchScheduledDeliverablesList/success',
  'scheduledDeliverables/fetchScheduledDeliverablesList/failure',
)<undefined, GetScheduledDeliverablesListSuccessPayload, undefined>();

export type SetMarkAsCompletedRequestPayload = {
  schedule_id: number;
  journal_entries_status: number;
};
export const setMarkAsCompleted = createAsyncAction(
  'scheduledDeliverables/setMarkAsCompleted/request',
  'scheduledDeliverables/setMarkAsCompleted/success',
  'scheduledDeliverables/setMarkAsCompleted/failure',
)<SetMarkAsCompletedRequestPayload, undefined, undefined>();

export type DeleteDeliverableRequestPayload = {
  schedule_id: number;
  modalId?: string;
};
export const deleteDeliverable = createAsyncAction(
  'scheduledDeliverables/deleteDeliverable/request',
  'scheduledDeliverables/deleteDeliverable/success',
  'scheduledDeliverables/deleteDeliverable/failure',
)<DeleteDeliverableRequestPayload, undefined, undefined>();

export type DeleteDeliverablesRequestPayload = {
  selected_schedule_ids: number[];
  schedule_id: number;
  modalId?: string;
};
export const deleteDeliverables = createAsyncAction(
  'scheduledDeliverables/deleteDeliverables/request',
  'scheduledDeliverables/deleteDeliverables/success',
  'scheduledDeliverables/deleteDeliverables/failure',
)<DeleteDeliverablesRequestPayload, undefined, undefined>();

export type GetScheduledDeliverableRequestPayload = {
  schedule_id: number;
};
export type GetScheduledDeliverableSuccessPayload = {
  data: ScheduledDeliverable;
};
export const getScheduledDeliverable = createAsyncAction(
  'scheduledDeliverables/getScheduledDeliverable/request',
  'scheduledDeliverables/getScheduledDeliverable/success',
  'scheduledDeliverables/getScheduledDeliverable/failure',
)<
  GetScheduledDeliverableRequestPayload,
  GetScheduledDeliverableSuccessPayload,
  GetScheduledDeliverableRequestPayload
>();

export type GetActivityLogsRequestPayload = {
  schedule_id: number;
};
export type GetActivityLogsSuccessPayload = {
  data: ScheduleDeliverableLog[];
  schedule_id: number;
};
export const getActivityLogs = createAsyncAction(
  'scheduledDeliverables/getActivityLogs/request',
  'scheduledDeliverables/getActivityLogs/success',
  'scheduledDeliverables/getActivityLogs/failure',
)<
  GetActivityLogsRequestPayload,
  GetActivityLogsSuccessPayload,
  GetActivityLogsRequestPayload
>();

export type RunDeliverableReportRequestPayload = {
  data: ScheduleDeliverableParams;
  isSchedule?: boolean;
  schedule_id: number;
};
export type RunDeliverableReportSuccessPayload = {
  schedule_id: number;
};
export const runDeliverableReport = createAsyncAction(
  'scheduledDeliverables/runDeliverableReport/request',
  'scheduledDeliverables/runDeliverableReport/success',
  'scheduledDeliverables/runDeliverableReport/failure',
)<
  RunDeliverableReportRequestPayload,
  RunDeliverableReportSuccessPayload,
  RunDeliverableReportSuccessPayload
>();

export type GetClientEmailsRequestPayload = {
  schedule_id: number;
};
export type GetClientEmailsSuccessPayload = {
  data: ScheduleDeliverableLog[];
  schedule_id: number;
};
export const getClientEmails = createAsyncAction(
  'scheduledDeliverables/getClientEmails/request',
  'scheduledDeliverables/getClientEmails/success',
  'scheduledDeliverables/getClientEmails/failure',
)<
  GetClientEmailsRequestPayload,
  GetClientEmailsSuccessPayload,
  GetClientEmailsRequestPayload
>();

export type SendClientEmailsRequestPayload = {
  sendEmailPayload: {
    schedule_id: number;
    emails: string[];
    download_id: number | null;
    company_names?: string[];
    company_entity_names?: string[];
  };
  newActivityPayload: { schedule_id: number; actor: string; message: string };
};
export const sendClientEmails = createAsyncAction(
  'scheduledDeliverables/sendClientEmails/request',
  'scheduledDeliverables/sendClientEmails/success',
  'scheduledDeliverables/sendClientEmails/failure',
)<SendClientEmailsRequestPayload, undefined, undefined>();

export type GetReportListDownloadRequestPayload = {
  schedule_id: number;
};
export type GetReportListDownloadSuccessPayload = {
  data: ReportDownloadItem[];
  schedule_id: number;
};
export const getReportListDownload = createAsyncAction(
  'scheduledDeliverables/getReportListDownload/request',
  'scheduledDeliverables/getReportListDownload/success',
  'scheduledDeliverables/getReportListDownload/failure',
)<
  GetReportListDownloadRequestPayload,
  GetReportListDownloadSuccessPayload,
  GetReportListDownloadRequestPayload
>();

export type DeleteReportDownloadRequestPayload = {
  schedule_id: number;
  download_ids: number[];
  modalId?: string;
};
export const deleteReportDownload = createAsyncAction(
  'scheduledDeliverables/deleteReportDownload/request',
  'scheduledDeliverables/deleteReportDownload/success',
  'scheduledDeliverables/deleteReportDownload/failure',
)<DeleteReportDownloadRequestPayload, undefined, undefined>();

export type GetNewTradesRequestPayload = {
  schedule_id: number;
};
export type GetNewTradesSuccessPayload = {
  data: number[];
  schedule_id: number;
};
export const getNewTrades = createAsyncAction(
  'scheduledDeliverables/getNewTrades/request',
  'scheduledDeliverables/getNewTrades/success',
  'scheduledDeliverables/getNewTrades/failure',
)<
  GetNewTradesRequestPayload,
  GetNewTradesSuccessPayload,
  GetNewTradesRequestPayload
>();

export type CloneScheduledDeliverablesRequestPayload = {
  schedule_id: number;
  scheduled_date: string;
};
export const cloneScheduledDeliverable = createAsyncAction(
  'scheduledDeliverables/cloneScheduledDeliverable/request',
  'scheduledDeliverables/cloneScheduledDeliverable/success',
  'scheduledDeliverables/cloneScheduledDeliverable/failure',
)<CloneScheduledDeliverablesRequestPayload, undefined, undefined>();

export type GetScheduledDeliverableTransactionsRequestPayload = {
  transaction_ids: number[] | null;
  company_entity_ids: string[] | null;
  start_date: string;
  end_date: string;
};
export type GetScheduledDeliverableTransactionsSuccessPayload = {
  data: TransactionItemProps[];
};
export const getScheduledDeliverableTransactions = createAsyncAction(
  'scheduledDeliverables/getScheduledDeliverableTransactions/request',
  'scheduledDeliverables/getScheduledDeliverableTransactions/success',
  'scheduledDeliverables/getScheduledDeliverableTransactions/failure',
)<
  GetScheduledDeliverableTransactionsRequestPayload,
  GetScheduledDeliverableTransactionsSuccessPayload,
  undefined
>();

export type CreateUpdateScheduledDeliverableRequestPayload = {
  data: CreateScheduleDeliverableParamsProps;
};
export const createUpdateScheduledDeliverable = createAsyncAction(
  'scheduledDeliverables/createUpdateScheduledDeliverable/request',
  'scheduledDeliverables/createUpdateScheduledDeliverable/success',
  'scheduledDeliverables/createUpdateScheduledDeliverable/failure',
)<CreateUpdateScheduledDeliverableRequestPayload, undefined, undefined>();

export type GetScheduledDeliverablesTransactionsRequestPayload = {
  trade_id: number;
};
export type GetScheduledDeliverablesTransactionsSuccessPayload = {
  data: string[];
  trade_id: number;
};
export const getScheduledDeliverablesTransactions = createAsyncAction(
  'scheduledDeliverables/getScheduledDeliverablesTransactions/request',
  'scheduledDeliverables/getScheduledDeliverablesTransactions/success',
  'scheduledDeliverables/getScheduledDeliverablesTransactions/failure',
)<
  GetScheduledDeliverablesTransactionsRequestPayload,
  GetScheduledDeliverablesTransactionsSuccessPayload,
  GetScheduledDeliverablesTransactionsRequestPayload
>();

export type GetScheduledDeliverablesTestReportsRequestPayload = {
  schedule_id: number;
};
export type GetScheduledDeliverablesTestReportsSuccessPayload = {
  data: ScheduledDeliverablesTestReports[];
  schedule_id: number;
};
export const getScheduledDeliverablesTestReports = createAsyncAction(
  'scheduledDeliverables/getScheduledDeliverablesTestReports/request',
  'scheduledDeliverables/getScheduledDeliverablesTestReports/success',
  'scheduledDeliverables/getScheduledDeliverablesTestReports/failure',
)<
  GetScheduledDeliverablesTestReportsRequestPayload,
  GetScheduledDeliverablesTestReportsSuccessPayload,
  GetScheduledDeliverablesTestReportsRequestPayload
>();

export type GetScheduledDeliverablesTestReportRequestPayload = {
  schedule_id: number;
  report_id: number;
};

export type GetScheduledDeliverablesTestReportSuccessPayload = {
  data: ScheduledDeliverablesTestReport;
  schedule_id: number;
  report_id: number;
};
export const getScheduledDeliverablesTestReport = createAsyncAction(
  'scheduledDeliverables/getScheduledDeliverablesTestReport/request',
  'scheduledDeliverables/getScheduledDeliverablesTestReport/success',
  'scheduledDeliverables/getScheduledDeliverablesTestReport/failure',
)<
  GetScheduledDeliverablesTestReportRequestPayload,
  GetScheduledDeliverablesTestReportSuccessPayload,
  GetScheduledDeliverablesTestReportRequestPayload
>();

export type RerunValidationsRequestPayload = {
  schedule_id: number;
  as_of_date: string;
};

export const rerunValidations = createAsyncAction(
  'scheduledDeliverables/rerunValidations/request',
  'scheduledDeliverables/rerunValidations/success',
  'scheduledDeliverables/rerunValidations/failure',
)<
  RerunValidationsRequestPayload,
  RerunValidationsRequestPayload,
  RerunValidationsRequestPayload
>();
